import { useState, useCallback } from "react";

export const PINNED_STORAGE_KEY = "aishield:nav-pinned";
export const LEGACY_STORAGE_KEY = "aishield:nav-expanded";

/**
 * Safely reads the persisted pinned preference from localStorage.
 * Defaults to false (automatic collapse on desktop).
 * Crucially: does NOT interpret previous 'aishield:nav-expanded' as pinned.
 */
export function getInitialNavPinned(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    const saved = window.localStorage.getItem(PINNED_STORAGE_KEY);
    if (saved !== null) {
      return saved === "true";
    }
  } catch {
    // Graceful fallback if localStorage is disabled or restricted
  }
  return false;
}

/**
 * Kept for backward compatibility with previous helpers.
 * Under the new hover-first specification, default resting state is collapsed (false).
 */
export function getInitialNavExpanded(): boolean {
  return getInitialNavPinned();
}

/**
 * Hook managing primary navigation pinned state (desktop) and drawer state (mobile).
 */
export function useNavigationState() {
  const [isPinned, setIsPinned] = useState<boolean>(getInitialNavPinned);
  const [isMobileOpen, setIsMobileOpen] = useState<boolean>(false);

  const togglePinned = useCallback(() => {
    setIsPinned((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(PINNED_STORAGE_KEY, String(next));
      } catch {
        // Ignore storage errors in restricted contexts
      }
      return next;
    });
  }, []);

  const openMobile = useCallback(() => {
    setIsMobileOpen(true);
  }, []);

  const closeMobile = useCallback(() => {
    setIsMobileOpen(false);
  }, []);

  return {
    isPinned,
    togglePinned,
    isMobileOpen,
    openMobile,
    closeMobile,
    // Aliases for backward compatibility
    isExpanded: isPinned,
    toggleExpanded: togglePinned,
  };
}
